import { api } from './api';
import type { ApiResponse, PaginatedResponse } from '@soundbyte/types';

/**
 * SoundByte Request (Originals) - Backend expected format
 */
interface CreateSoundByteRequestInput {
  projectName: string;
  projectDescription: string;
  inspiration: string;
  storyPillars?: {
    meaning?: string;
    places?: string;
    passions?: string;
    people?: string;
    personality?: string;
    favorites?: string;
  };
  colorPreferences: string[];
  stylePreferences: string[];
  preferredSize: string;
  preferredMaterial: string;
  wantsFraming?: boolean;
  desiredTimeline: string;
  specificDate?: string;
  isGift: boolean;
  giftOccasion?: string;
  intendedUse?: string;
  additionalNotes?: string;
  voiceUrl?: string;
  referenceFiles?: {
    id: string;
    url: string;
    name: string;
    type: string;
    caption?: string;
  }[];
}

/**
 * Sonic Request - Backend expected format
 */
interface CreateSonicRequestInput {
  name: string;
  prompt: string;
  influenceTags: string[];
  artStyle?: 'urban-skyline' | 'nature' | 'cosmic' | 'ocean' | 'abstract' | 'forest' | 'desert';
  voiceUrl?: string;
  voiceDuration?: number;
  waveformGuideBase64?: string; // Client-generated waveform guide image
  formatType: 'digital' | 'physical' | 'phygital';
  packageSize: 'small' | 'medium' | 'large';
  packageMaterial: 'paper' | 'canvas' | 'aluminium-silver' | 'aluminium-gold';
  withFraming: boolean;
  deliveryOption: 'standard' | 'fast-track';
  shippingAddress?: {
    fullName: string;
    addressLine1: string;
    addressLine2?: string;
    city: string;
    state?: string;
    postalCode: string;
    country: string;
    phone?: string;
  };
  totalPrice: number;
  breakdown?: Record<string, unknown>;
}

/**
 * Tier data stored in storyPillars._tierData
 */
export interface SoundByteTierData {
  tier: 'low' | 'medium' | 'high';
  personalImageUrl?: string;
  lowTierImages?: Array<{ id: string; url: string; name: string }>;
  selectedUpgrades?: string[];
  briefDescription?: string;
  totalPrice?: number;
  retainerAmount?: number;
  retainerPaid?: boolean;
  retainerPaidAt?: string;
  balanceAmount?: number;
  balancePaid?: boolean;
  balancePaidAt?: string;
  fullPaymentPaid?: boolean;
  fullPaymentPaidAt?: string;
  subStatus?: string;
  digitalArtworkUrl?: string;
  refinementCount?: number;
  maxRefinements?: number;
  // Artwork approval fields
  artworkApproved?: boolean;
  artworkApprovedAt?: string;
  // Refinement history
  lastRefinementNotes?: string;
  lastRefinementRequestedAt?: string;
  refinementHistory?: Array<{ notes: string; requestedAt: string }>;
  // WhatsApp chat link (set by backend after retainer payment)
  whatsappChatLink?: string;
  // Physical delivery fields
  physicalDeliveryRequested?: boolean;
  physicalDeliveryPaid?: boolean;
  physicalDeliveryPaidAt?: string;
  // Physical config (size, material, framing, shipping address) - from checkout flow
  physicalConfig?: {
    sizeId: string;
    materialId: string;
    withFraming: boolean;
    shippingAddress: {
      fullName: string;
      line1: string;
      line2?: string;
      city: string;
      state?: string;
      postalCode: string;
      country: string;
      phone?: string;
    };
    price: number;
    printStatus?: 'pending' | 'in_progress' | 'delivered';
  };
  // Completion fields
  finalArtworkUrl?: string;
  completedAt?: string;
}

/**
 * SoundByte Request Response
 */
export interface SoundByteRequest {
  id: string;
  userId: string;
  projectName: string;
  projectDescription: string;
  status: 'pending' | 'under_review' | 'quote_sent' | 'in_progress' | 'awaiting_payment' | 'completed' | 'cancelled';
  quoteMin?: number;
  quoteMax?: number;
  estimatedDelivery?: string;
  adminNotes?: string;
  digitalOutputUrl?: string;
  trackingNumber?: string;
  createdAt: string;
  updatedAt: string;
  // Tier data stored in storyPillars
  storyPillars?: {
    _tierData?: SoundByteTierData;
    [key: string]: unknown;
  };
}

/**
 * Sonic Request Response
 */
export interface SonicRequest {
  id: string;
  userId: string;
  name: string;
  prompt: string;
  status: 'pending' | 'under_review' | 'quote_sent' | 'in_progress' | 'awaiting_payment' | 'completed' | 'cancelled';
  totalPrice: number;
  digitalOutputUrl?: string;
  trackingNumber?: string;
  estimatedDeliveryDate?: string;
  // 2-Phase flow fields
  digitalStatus?: 'pending' | 'in_progress' | 'ready' | 'failed';
  digitalPrice?: number;
  fastTrack?: boolean;
  digitalReadyAt?: string;
  // Payment fields
  paymentStatus?: 'pending' | 'paid' | 'failed' | 'refunded';
  paidAt?: string;
  createdAt: string;
  updatedAt: string;
}

/**
 * Express availability plus the studio's promise dates for an order placed now.
 */
export interface ExpressAvailabilityResponse {
  available: boolean;
  reason?: 'vacation' | 'express_full' | 'day_full';
  /** Working days in the standard promise, including any vacation extension. */
  standardWorkingDays?: number;
  /** ISO instants: end of the working day each option is due by. */
  readyBy?: {
    standard: string;
    express: string | null;
  };
}

/**
 * Digital Status Response (2-Phase flow)
 */
export interface SonicDigitalStatus {
  id: string;
  name: string;
  prompt: string;
  digitalStatus: 'pending' | 'in_progress' | 'ready' | 'failed';
  digitalReadyAt?: string;
  fastTrack: boolean;
  digitalPrice?: number;
  paymentStatus: string;
  paidAt?: string;
  createdAt: string;
  estimatedDelivery: string;
}

/**
 * Reveal Data Response (2-Phase flow)
 */
export interface SonicRevealArtwork {
  id: string;
  version: number;
  isSelected: boolean;
  /** A print of this interpretation has been ordered. The others stay buyable. */
  hasPrintOrder?: boolean;
}

export interface SonicRevealData {
  id: string;
  name: string;
  prompt: string;
  readyAt?: string;
  hasArtwork: boolean;
  formatType: 'digital' | 'physical' | 'phygital';
  createdAt: string;
  // Delivered creations (up to 3). Customer views all and picks a favorite.
  artworks?: SonicRevealArtwork[];
  selectedArtworkId?: string | null;
  /**
   * Whether the chosen interpretation can still be changed. It locks once a
   * print has been paid for, because that print is the one the bundle covers.
   */
  canChangeSelection?: boolean;
  // Physical order info (one order per artwork limit)
  hasPhysicalOrder?: boolean;
  physicalOrder?: {
    id: string;
    status: string;
    paymentStatus: string;
    createdAt: string;
  } | null;
}

/**
 * Physical Order Input
 */
interface CreatePhysicalOrderInput {
  sizeId: string;
  materialId: string;
  withFraming: boolean;
  shippingAddress: {
    fullName: string;
    line1: string;
    line2?: string;
    city: string;
    state?: string;
    postalCode: string;
    country: string;
    phone?: string;
  };
  totalPrice: number;
  breakdown?: Record<string, unknown>;
}

/**
 * Physical Order Response
 */
export interface SonicPhysicalOrder {
  id: string;
  sonicRequestId: string;
  sizeId: string;
  materialId: string;
  withFraming: boolean;
  shippingAddress: Record<string, string>;
  totalPrice: number;
  breakdown?: Record<string, unknown>;
  paymentStatus: 'pending' | 'paid' | 'refunded';
  status: 'pending' | 'paid' | 'in_production' | 'shipped' | 'delivered' | 'cancelled';
  trackingNumber?: string;
  createdAt: string;
  updatedAt: string;
}

/**
 * Request Service
 *
 * Handles SoundByte Originals and Sonic Identity request submissions.
 */
class RequestService {
  // ============================================
  // SoundByte Originals Requests
  // ============================================

  /**
   * Create a new SoundByte Originals request
   */
  async createSoundByteRequest(data: CreateSoundByteRequestInput): Promise<ApiResponse<SoundByteRequest>> {
    return api.post<SoundByteRequest>('/soundbyte-requests', data);
  }

  /**
   * Upload voice file for SoundByte request
   */
  async uploadSoundByteVoice(file: File): Promise<ApiResponse<{ id: string; url: string }>> {
    return api.upload<{ id: string; url: string }>('/soundbyte-requests/voice', file, 'voice');
  }

  /**
   * Upload reference files for SoundByte request
   */
  async uploadSoundByteReferences(files: File[]): Promise<ApiResponse<{ files: { id: string; url: string }[] }>> {
    return api.uploadMultiple<{ files: { id: string; url: string }[] }>(
      '/soundbyte-requests/references',
      files,
      'files'
    );
  }

  /**
   * Get user's SoundByte requests
   */
  async getSoundByteRequests(
    page: number = 1,
    pageSize: number = 10
  ): Promise<ApiResponse<PaginatedResponse<SoundByteRequest>>> {
    return api.get<PaginatedResponse<SoundByteRequest>>('/soundbyte-requests', { page, pageSize });
  }

  /**
   * Get single SoundByte request by ID
   */
  async getSoundByteRequest(id: string): Promise<ApiResponse<SoundByteRequest>> {
    return api.get<SoundByteRequest>(`/soundbyte-requests/${id}`);
  }

  /**
   * Update a SoundByte request (before quote is sent)
   */
  async updateSoundByteRequest(
    id: string,
    data: Partial<CreateSoundByteRequestInput>
  ): Promise<ApiResponse<SoundByteRequest>> {
    return api.patch<SoundByteRequest>(`/soundbyte-requests/${id}`, data);
  }

  // ============================================
  // Sonic Identity Requests
  // ============================================

  /**
   * Create a new Sonic Identity request
   */
  async createSonicRequest(data: CreateSonicRequestInput): Promise<ApiResponse<SonicRequest>> {
    return api.post<SonicRequest>('/sonic-requests', data);
  }

  /**
   * Whether Express Creation can be sold right now. Express is a
   * one-working-day promise, so it is only offered while the studio is open
   * and the immediate queue day still has an Express slot.
   *
   * Also carries the dates an order placed now would be promised for, from
   * the studio calendar. Absent when the backend fails closed.
   */
  async getExpressAvailability(): Promise<ApiResponse<ExpressAvailabilityResponse>> {
    return api.get('/sonic-requests/express-availability');
  }

  /**
   * Upload voice file for Sonic request
   */
  async uploadSonicVoice(file: File): Promise<ApiResponse<{ id: string; url: string }>> {
    return api.upload<{ id: string; url: string }>('/sonic-requests/voice', file, 'voice');
  }

  /**
   * Get user's Sonic requests
   */
  async getSonicRequests(
    page: number = 1,
    pageSize: number = 10
  ): Promise<ApiResponse<PaginatedResponse<SonicRequest>>> {
    return api.get<PaginatedResponse<SonicRequest>>('/sonic-requests', { page, pageSize });
  }

  /**
   * Get single Sonic request by ID
   */
  async getSonicRequest(id: string): Promise<ApiResponse<SonicRequest>> {
    return api.get<SonicRequest>(`/sonic-requests/${id}`);
  }

  /**
   * Process payment for Sonic request (placeholder)
   */
  async processSonicPayment(id: string): Promise<ApiResponse<{ message: string; requestId: string }>> {
    return api.post<{ message: string; requestId: string }>(`/sonic-requests/${id}/payment`);
  }

  // ============================================
  // Cancel Requests
  // ============================================

  /**
   * Cancel a SoundByte Originals request
   * Only works for pending or under_review status
   */
  async cancelSoundByteRequest(
    id: string,
    reason?: string
  ): Promise<ApiResponse<{ message: string; request: SoundByteRequest }>> {
    return api.post<{ message: string; request: SoundByteRequest }>(
      `/soundbyte-requests/${id}/cancel`,
      { reason }
    );
  }

  /**
   * Cancel a Sonic Identity request
   * Only works for pending or under_review status
   */
  async cancelSonicRequest(
    id: string,
    reason?: string
  ): Promise<ApiResponse<{ message: string; request: SonicRequest }>> {
    return api.post<{ message: string; request: SonicRequest }>(
      `/sonic-requests/${id}/cancel`,
      { reason }
    );
  }

  // ============================================
  // SoundByte Originals - Stripe Checkout
  // ============================================

  /**
   * Create a Stripe checkout session for SoundByte Originals quote payment
   */
  async createSoundByteCheckoutSession(data: {
    requestId: string;
    successUrl: string;
    cancelUrl: string;
  }): Promise<ApiResponse<{ sessionId: string; url: string }>> {
    return api.post<{ sessionId: string; url: string }>('/soundbyte-requests/create-checkout-session', data);
  }

  /**
   * Create a Stripe checkout session for Low tier full payment
   */
  async createLowTierCheckoutSession(data: {
    requestId: string;
    totalPrice: number;
    projectName: string;
    selectedUpgrades: string[];
    successUrl: string;
    cancelUrl: string;
  }): Promise<ApiResponse<{ sessionId: string; url: string }>> {
    return api.post<{ sessionId: string; url: string }>('/soundbyte-requests/low-tier-checkout', data);
  }

  /**
   * Create a Stripe checkout session for Medium/High tier retainer payment
   */
  async createRetainerCheckoutSession(data: {
    requestId: string;
    retainerAmount: number;
    projectName: string;
    tier: 'medium' | 'high';
    successUrl: string;
    cancelUrl: string;
  }): Promise<ApiResponse<{ sessionId: string; url: string }>> {
    return api.post<{ sessionId: string; url: string }>('/soundbyte-requests/retainer-checkout', data);
  }

  /**
   * Create a Stripe checkout session for Medium/High tier balance payment
   */
  async createBalanceCheckoutSession(data: {
    requestId: string;
    balanceAmount: number;
    projectName: string;
    tier: 'medium' | 'high';
    successUrl: string;
    cancelUrl: string;
  }): Promise<ApiResponse<{ sessionId: string; url: string }>> {
    return api.post<{ sessionId: string; url: string }>('/soundbyte-requests/balance-checkout', data);
  }

  /**
   * Create a Stripe checkout session for optional physical delivery
   */
  async createPhysicalDeliveryCheckoutSession(data: {
    requestId: string;
    amount: number;
    projectName: string;
    successUrl: string;
    cancelUrl: string;
  }): Promise<ApiResponse<{ sessionId: string; url: string }>> {
    return api.post<{ sessionId: string; url: string }>('/soundbyte-requests/physical-delivery-checkout', data);
  }

  /**
   * Verify a SoundByte Originals checkout session after payment
   */
  async verifySoundByteCheckout(sessionId: string): Promise<ApiResponse<{
    success: boolean;
    request?: {
      id: string;
      projectName: string;
      status: string;
    };
  }>> {
    return api.post<{
      success: boolean;
      request?: {
        id: string;
        projectName: string;
        status: string;
      };
    }>('/soundbyte-requests/verify-checkout', { sessionId });
  }

  // ============================================
  // SoundByte Originals - Artwork Approval/Refinement
  // ============================================

  /**
   * Approve artwork (Medium/High tier)
   * After approval, user can proceed to balance payment
   */
  async approveArtwork(requestId: string): Promise<ApiResponse<{
    message: string;
    request: SoundByteRequest;
  }>> {
    return api.post<{ message: string; request: SoundByteRequest }>(
      `/soundbyte-requests/${requestId}/approve-artwork`
    );
  }

  /**
   * Request artwork refinement (Medium/High tier)
   * Medium tier: Up to 2 refinements
   * High tier: Unlimited refinements
   */
  async requestRefinement(
    requestId: string,
    notes: string
  ): Promise<ApiResponse<{
    message: string;
    request: SoundByteRequest;
  }>> {
    return api.post<{ message: string; request: SoundByteRequest }>(
      `/soundbyte-requests/${requestId}/request-refinement`,
      { notes }
    );
  }

  // ============================================
  // Sonic Identity - 2-Phase Flow
  // ============================================

  /**
   * Get digital creation status for a Sonic request
   * Used to poll for artwork ready status
   */
  async getSonicDigitalStatus(id: string): Promise<ApiResponse<SonicDigitalStatus>> {
    return api.get<SonicDigitalStatus>(`/sonic-requests/${id}/status`);
  }

  /**
   * Get reveal data for a completed Sonic request
   * Returns info about the completed artwork (use image proxy endpoint for actual image)
   */
  async getSonicRevealData(id: string): Promise<ApiResponse<SonicRevealData>> {
    return api.get<SonicRevealData>(`/sonic-requests/${id}/reveal`);
  }

  /**
   * Pick a favorite among the delivered Sonic creations. The chosen artwork
   * becomes the primary used for download and the physical-print upsell.
   */
  async selectSonicArtwork(
    id: string,
    artworkId: string
  ): Promise<ApiResponse<{ success: boolean; selectedArtworkId: string }>> {
    return api.post(`/sonic-requests/${id}/select-artwork`, { artworkId });
  }

  /**
   * Create a physical print order for a Sonic Identity
   * Only available after digital artwork is ready
   */
  async createPhysicalOrder(
    requestId: string,
    data: CreatePhysicalOrderInput
  ): Promise<ApiResponse<SonicPhysicalOrder>> {
    return api.post<SonicPhysicalOrder>(`/sonic-requests/${requestId}/physical`, data);
  }

  /**
   * Get physical orders for a Sonic request
   */
  async getPhysicalOrders(requestId: string): Promise<ApiResponse<SonicPhysicalOrder[]>> {
    return api.get<SonicPhysicalOrder[]>(`/sonic-requests/${requestId}/physical`);
  }

  /**
   * Get a specific physical order
   */
  async getPhysicalOrder(orderId: string): Promise<ApiResponse<SonicPhysicalOrder>> {
    return api.get<SonicPhysicalOrder>(`/sonic-requests/physical/${orderId}`);
  }

  /**
   * Verify Stripe checkout session and create physical order
   * This is a fallback for when webhooks aren't available (e.g., local development)
   */
  async verifyPhysicalCheckout(
    requestId: string,
    sessionId: string
  ): Promise<ApiResponse<{ success: boolean; order?: SonicPhysicalOrder }>> {
    return api.post<{ success: boolean; order?: SonicPhysicalOrder }>(
      `/sonic-requests/${requestId}/verify-physical-checkout`,
      { sessionId }
    );
  }

  /**
   * Create a Stripe checkout session for Sonic Identity physical print
   * Order is created only after payment is verified via webhook
   */
  async createPhysicalCheckoutSession(
    requestId: string,
    data: {
      editionId?: string;
      sizeId: string;
      materialId: string;
      withFraming: boolean;
      shippingAddress: {
        fullName: string;
        line1: string;
        line2?: string;
        city: string;
        state?: string;
        postalCode: string;
        country: string;
        phone?: string;
      };
      totalPrice: number;
      artworkName: string;
      /** Which interpretation to print. Omitted means the chosen one. */
      artworkId?: string | null;
      successUrl: string;
      cancelUrl: string;
    }
  ): Promise<
    ApiResponse<
      | { requiresPayment?: true; sessionId: string; url: string }
      | { requiresPayment: false; orderId: string }
    >
  > {
    // Under v2 the bundle already paid for this print, so keeping the included
    // size costs nothing and there is no Stripe session to redirect to — the
    // order is created straight away.
    return api.post<
      | { requiresPayment?: true; sessionId: string; url: string }
      | { requiresPayment: false; orderId: string }
    >(`/sonic-requests/${requestId}/physical-checkout`, data);
  }

  // ============================================
  // Sonic Identity - Stripe Checkout
  // ============================================

  /**
   * Create a Stripe checkout session for Sonic Identity order
   * Order is NOT created until payment is verified
   */
  async createSonicCheckoutSession(data: {
    name: string;
    prompt: string;
    meaning?: string;           // "Story Behind" text
    intendedUse?: string;       // 'personal' | 'gift' | 'corporate' | 'memorial' | 'celebration' | 'other'
    intendedUseOther?: string;  // Custom text if "other" is selected
    giftOccasion?: string;      // Gift occasion details
    influenceTags: string[];
    artStyle?: string;
    colorMode?: 'blackAndWhite' | 'colorful';  // Color mode selection
    colorPreferences?: string;  // User's color preferences description (when colorful)
    colorPaletteId?: string;    // Palette-based color selection (legacy)
    selectedColors?: string[];  // User-selected colors (max 3, legacy)
    voiceUrl?: string;
    voiceDuration?: number;
    waveformGuideBase64?: string;
    waveformAmplitudes?: number[];
    formatType: 'digital' | 'physical' | 'phygital';
    packageSize: string;
    packageMaterial: string;
    withFraming: boolean;
    deliveryOption: 'standard' | 'fast-track';
    shippingAddress?: {
      fullName: string;
      line1: string;
      line2?: string;
      city: string;
      state?: string;
      postalCode: string;
      country: string;
      phone?: string;
    };
    totalPrice: number;
    breakdown?: Record<string, unknown>;
    successUrl: string;
    cancelUrl: string;
  }): Promise<ApiResponse<{ sessionId: string; url: string }>> {
    return api.post<{ sessionId: string; url: string }>('/sonic-requests/create-checkout-session', data);
  }

  /**
   * Verify a Sonic Identity checkout session after payment
   * Creates the order if payment was successful
   */
  async verifySonicCheckout(sessionId: string): Promise<ApiResponse<{
    success: boolean;
    request?: {
      id: string;
      name: string;
      digitalStatus: string;
    };
    user?: {
      id: string;
      email: string;
      fullName: string | null;
    };
  }>> {
    return api.post<{
      success: boolean;
      request?: {
        id: string;
        name: string;
        digitalStatus: string;
      };
      user?: {
        id: string;
        email: string;
        fullName: string | null;
      };
    }>('/sonic-requests/verify-checkout', { sessionId });
  }

  async toggleSonicFavorite(id: string): Promise<ApiResponse<{ isFavorite: boolean }>> {
    return api.patch<{ isFavorite: boolean }>(`/sonic-requests/${id}/favorite`);
  }

  async toggleSoundByteFavorite(id: string): Promise<ApiResponse<{ isFavorite: boolean }>> {
    return api.patch<{ isFavorite: boolean }>(`/soundbyte-requests/${id}/favorite`);
  }
}

export const requestService = new RequestService();
